// Thin typed wrappers for /api/v1. The dev server proxies /api → :8097
// (see vite.config.ts) and the backend sets the session cookie on /auth/me.
//
// All HTTP transport is delegated to @avagency/frontend/lib/api.apiFetch
// (credentials:include, JSON helpers, ApiError with body.detail derivation).
// This file owns ONLY the typed resource interfaces + URL templates + the
// AUTH_INVALIDATE_EVENT layer that bridges 401-from-product-API into a
// useMe refetch.
//
// TYPE CONTRACT: every interface here is derived from the as-built Go structs
// in internal/api/handler_archive.go, handler_integrations.go, and server.go.
// Do NOT add fields that the Go side does not emit.
import { apiFetch, ApiError } from '@avagency/frontend/lib/api';
export const fetchMe = () => listfitFetch(`/api/v1/auth/me`);
export const signInURL = (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    return `/api/v1/auth/signin?${q.toString()}`;
};
export const signOut = async (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    const res = await fetch(`/api/v1/auth/signout?${q.toString()}`, {
        method: 'POST',
        credentials: 'include',
    });
    const location = res.headers.get('Location') || returnTo;
    window.location.href = location;
};
export const parseOAuthCallbackStatus = () => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('av_oauth_status');
    if (!status)
        return { kind: 'none' };
    const errMsg = params.get('av_oauth_error') ?? '';
    if (status === 'success')
        return { kind: 'success' };
    return { kind: 'error', message: errMsg || 'OAuth flow failed' };
};
export const clearOAuthCallbackStatusFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('av_oauth_status') && !params.has('av_oauth_error'))
        return;
    params.delete('av_oauth_status');
    params.delete('av_oauth_error');
    const search = params.toString();
    const cleanURL = window.location.pathname +
        (search ? `?${search}` : '') +
        window.location.hash;
    window.history.replaceState({}, '', cleanURL);
};
export const fetchIntegrations = () => listfitFetch(`/api/v1/integrations`);
export const disconnectIntegration = (integrationID) => listfitFetch(`/api/v1/integrations/${encodeURIComponent(integrationID)}/disconnect`, { method: 'POST' });
// connectMailchimpByKey POSTs the API key to the platform ConnectByKey handler.
// The platform endpoint is /v1/oauth/mailchimp/connect-key; body: { tenant_id, key }.
// tenant_id in the body is overwritten by InjectTenantIntoOAuthStart (§4.4 body-spoof
// defence) — we include it for the schema but the session's tenant always wins.
export const connectMailchimpByKey = async (key) => {
    await listfitFetch('/v1/oauth/mailchimp/connect-key', {
        method: 'POST',
        json: { tenant_id: '', key },
    });
};
export const triggerScan = (req) => listfitFetch(`/api/v1/scans`, {
    method: 'POST',
    json: req,
});
export const fetchScans = () => listfitFetch(`/api/v1/scans`);
export const fetchScorecard = (scanID) => listfitFetch(`/api/v1/scans/${encodeURIComponent(scanID)}/scorecard`);
export const fetchArchivePreview = (scanID, req = {}) => listfitFetch(`/api/v1/scans/${encodeURIComponent(scanID)}/archive/preview`, { method: 'POST', json: req });
export const submitArchiveConfirm = (scanID, req) => listfitFetch(`/api/v1/scans/${encodeURIComponent(scanID)}/archive/confirm`, { method: 'POST', json: req });
export const isTerminalStatus = (s) => s === 'done';
export const fetchJobStatus = (runID) => listfitFetch(`/api/v1/jobs/${encodeURIComponent(runID)}`);
// ─── AUTH_INVALIDATE_EVENT ────────────────────────────────────────────────────
export const AUTH_INVALIDATE_EVENT = 'listfit:auth:invalidate';
// listfitFetch wraps apiFetch with the AUTH_INVALIDATE_EVENT side effect.
// Catches ApiError(401), dispatches the event (unless the 401 came from
// /auth/me itself — that 401 IS the session-gone signal, not a downstream
// consequence), then re-throws so call-site error handling stays intact.
async function listfitFetch(path, init) {
    try {
        return await apiFetch(path, init);
    }
    catch (err) {
        if (err instanceof ApiError) {
            if (err.status === 401 && !path.includes('/api/v1/auth/me')) {
                window.dispatchEvent(new CustomEvent(AUTH_INVALIDATE_EVENT));
            }
            const decorated = err;
            decorated.status = err.status;
            throw decorated;
        }
        throw err;
    }
}
// Re-export ApiError so call sites can do `instanceof ApiError` without
// a direct kit import.
export { ApiError };
